import React from "react";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  BarChart, Bar, XAxis, YAxis, Cell, ResponsiveContainer, Legend, Tooltip as RTooltip,
} from "recharts";
import { GrcCard, effectiveScore, pillarsArray, ragColor, effectiveRag, useIsMobile, isWorkflowPillar } from "./shared";

// Workflow pillars (P9/P10/P11) get a "W" suffix on the axis so users spot them at a glance.
const axisLabel = (code) => (isWorkflowPillar(code) ? `${code} ᵂ` : code);

export const PillarRadar = ({ pillars, sdf, waterline = 75, onPillarClick }) => {
  const isMobile = useIsMobile();
  const arr = pillarsArray(pillars);
  const data = arr.map((p) => ({
    code: p.code,
    axis: axisLabel(p.code),
    name: p.name,
    score: effectiveScore(p, sdf) ?? 0,
    waterline,
    rag: effectiveRag(p, sdf),
  }));

  return (
    <GrcCard
      title="Pillar Performance Radar (15 Pillars)"
      info="Pillar score % vs the 75% audit-benchmark waterline. P9, P10, P11 are non-scoring workflow pillars (marked ᵂ); P9 score is sourced from SDF Module B."
      infoTestId="info-pillar-radar"
      testId="widget-pillar-radar"
      className="lg:col-span-4"
    >
      <div className="h-[340px]" data-testid="pillar-radar-chart">
        <ResponsiveContainer width="100%" height="100%" minWidth={200} minHeight={200}>
          {isMobile ? (
            <BarChart data={data} layout="vertical" margin={{ left: 4, right: 16 }}>
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 10 }} />
              <YAxis type="category" dataKey="axis" width={38} tick={{ fontSize: 10 }} />
              <RTooltip
                formatter={(v) => [`${v}%`, "Score"]}
                labelFormatter={(l, p) => p?.[0]?.payload?.name || l}
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
              />
              <Bar dataKey="score" radius={[0, 4, 4, 0]} onClick={(d) => onPillarClick?.(d.code)}>
                {data.map((d) => (
                  <Cell key={d.code} fill={ragColor(d.rag)} cursor="pointer" />
                ))}
              </Bar>
            </BarChart>
          ) : (
            <RadarChart data={data} outerRadius="72%">
              <PolarGrid stroke="#E2E8F0" />
              <PolarAngleAxis dataKey="axis" tick={{ fontSize: 11, fill: "#475569" }} />
              <PolarRadiusAxis domain={[0, 100]} tickCount={5} tick={{ fontSize: 9, fill: "#94A3B8" }} />
              <Radar
                name="Your Score (%)"
                dataKey="score"
                stroke="#008080"
                fill="#008080"
                fillOpacity={0.18}
                strokeWidth={2}
                dot={{ r: 3, fill: "#008080" }}
              />
              <Radar
                name={`${waterline}% Waterline (Audit Benchmark)`}
                dataKey="waterline"
                stroke="#94A3B8"
                strokeDasharray="5 5"
                fill="none"
                strokeWidth={1.5}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <RTooltip
                formatter={(v, n) => [`${v}%`, n]}
                labelFormatter={(l, p) => p?.[0]?.payload?.name || l}
                contentStyle={{ fontSize: 12, borderRadius: 8 }}
              />
            </RadarChart>
          )}
        </ResponsiveContainer>
      </div>
      <p className="mt-2 text-[11px] text-slate-500" data-testid="radar-footnote">
        ᵂ Workflow pillars (P9, P10, P11) — non-scoring. P9 score sourced from SDF Module B.
      </p>
    </GrcCard>
  );
};
